import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  DOMAIN,
  REASON,
  reportError,
  reportFailure,
  reportWarning,
} from '~/services/telemetry';
import type { OrderStore } from '~/stores/OrderStore/OrderStore';
import type { UserStore } from '~/stores/UserStore';

import { httpStatusOf } from './api/HttpMembershipGateway';
import { membershipReturnUrl } from './domain/clubEntry';
import type {
  MembershipPurchaseEvent,
  MembershipPurchaseIncident,
} from './domain/purchase';
import {
  createCardFormFactory,
  createGateway,
  createMarkers,
  reportFormIncident,
} from './infrastructure';
import { MembershipPurchaseStore } from './store/MembershipPurchaseStore';
import {
  type MembershipActivationOutcome,
  MembershipStore,
} from './store/MembershipStore';

/** Analytics and telemetry of a `pending` purchase that settled while the page polled. */
const reportActivation = (outcome: MembershipActivationOutcome): void => {
  if (outcome.kind === 'failed') {
    analyticsService.track(AnalyticsEvent.MEMBERSHIP_PURCHASE_FAILED, {
      planCode: outcome.planCode,
      reason: 'charge_failed',
    });
    return;
  }
  if (outcome.kind === 'timeout') {
    reportWarning(
      DOMAIN.membership,
      'Pending purchase still processing after a minute',
      { reason: REASON.membership.pendingTimeout },
    );
  }
};

/**
 * Composition root of the membership module: binds the stores to the app's
 * HTTP client, the courier zone of the current ETA, the selected delivery
 * address, session storage, analytics and telemetry. A 401 is left to the
 * session refresh flow.
 */
export const createMembershipStore = (
  orderStore: OrderStore,
  userStore: UserStore,
): MembershipStore =>
  new MembershipStore({
    gateway: createGateway(),
    getCourierZoneId: () => orderStore.courierZoneId,
    hasDeliveryAddress: () => userStore.deliveryAddress !== null,
    isAuthorized: () => userStore.isAuthorized,
    isEtaPending: () =>
      orderStore.etaCalculation === null && !orderStore.isDeliveryUnavailable,
    onError: (error, request) => {
      const status = httpStatusOf(error);
      if (status === 401) {
        return;
      }
      reportError(DOMAIN.membership, error, {
        reason: REASON.membership.loadFailed,
        level: 'warning',
        once: true,
        fingerprint: [request],
        extras: { status },
      });
    },
    markers: createMarkers(userStore),
    onActivationSettled: reportActivation,
  });

const trackPurchase = (event: MembershipPurchaseEvent): void => {
  switch (event.name) {
    case 'cardSubmitted':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_CARD_SUBMITTED,
        event.payload,
      );
      return;
    case 'threeDSShown':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_3DS_SHOWN,
        event.payload,
      );
      return;
    case 'purchaseSucceeded':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_PURCHASE_SUCCEEDED,
        event.payload,
      );
      return;
    case 'purchaseFailed':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_PURCHASE_FAILED,
        event.payload,
      );
  }
};

/** Sentry reports of Join. Extras carry the classification only — never a payment field. */
const reportPurchase = (incident: MembershipPurchaseIncident): void => {
  switch (incident.reason) {
    case 'purchaseFailed': {
      const report =
        incident.level === 'warning' ? reportWarning : reportFailure;
      report(DOMAIN.membership, 'Purchase failed', {
        reason: REASON.membership.purchaseFailed,
        fingerprint: [incident.kind],
        extras: {
          kind: incident.kind,
          status: incident.status,
          code: incident.code,
          upstreamOperation: incident.upstream?.operation,
          upstreamStatus: incident.upstream?.upstreamStatus,
        },
      });
      return;
    }
    case 'cardRefused':
      reportWarning(DOMAIN.membership, 'Card refused', {
        reason: REASON.membership.cardRefused,
        fingerprint: [incident.code ?? String(incident.status ?? 'unknown')],
        extras: { status: incident.status, code: incident.code },
      });
      return;
    case 'confirmFailed':
      reportFailure(DOMAIN.membership, 'Card confirmation failed', {
        reason: REASON.membership.confirmFailed,
        fingerprint: [incident.kind],
        extras: {
          kind: incident.kind,
          status: incident.status,
          code: incident.code,
          upstreamOperation: incident.upstream?.operation,
          upstreamStatus: incident.upstream?.upstreamStatus,
        },
      });
      return;
    case 'adyenInitFailed':
    case 'cardFormChunkFailed':
      reportFormIncident(incident);
  }
};

/**
 * Composition root of Join (`?modal=club-join`): the purchase store over the never-retried
 * gateway, the Adyen card form loaded on first mount, session markers, and the
 * membership store it refreshes once a purchase settles.
 */
export const createMembershipPurchaseStore = (
  membershipStore: MembershipStore,
  userStore: UserStore,
): MembershipPurchaseStore =>
  new MembershipPurchaseStore({
    gateway: createGateway(),
    createCardForm: createCardFormFactory(userStore),
    markers: createMarkers(userStore),
    returnUrl: () => membershipReturnUrl(window.location.origin),
    onMembershipChanged: () => {
      void membershipStore.refresh();
    },
    reloadPage: () => {
      window.location.reload();
    },
    onPlansStale: () => {
      void membershipStore.loadPlans();
    },
    track: trackPurchase,
    report: reportPurchase,
  });
